/**
 * 条形码和二维码生成器模块
 * 
 * 本模块提供了生成各种类型条形码（CODE128、CODE39、EAN13、EAN8）和二维码的功能。
 * 使用 JsBarcode 和 qrcode 库来生成图像，并返回 base64 格式的 Data URL。
 */

import JsBarcode from 'jsbarcode';
import QRCode, { QRCodeToDataURLOptions } from 'qrcode';

/**
 * 条形码类型映射表
 * 将小写的条形码类型名称映射到 JsBarcode 库所需的大写格式
 */
const FORMAT_MAP: { [key: string]: string } = {
    'code128': 'CODE128',
    'code39': 'CODE39',
    'ean13': 'EAN13',
    'ean8': 'EAN8'
};

/**
 * 各类条形码的默认值
 * 不同类型的条形码对数据格式有特定要求，这里提供了符合规范的默认值
 */
const DEFAULT_VALUES: { [key: string]: string } = {
    'code128': '123456789',
    'code39': '123456789',
    'ean13': '6901234567892',  // EAN13 需要 13 位数字
    'ean8': '69012341'         // EAN8 需要 8 位数字
};

/**
 * 生成二维码
 *
 * @param value - 要编码到二维码中的文本内容
 * @param options - 二维码生成选项
 * @returns Promise<string> - 返回二维码的 base64 Data URL
 */
export async function generateQRCode(value: string, options?: Partial<QRCodeToDataURLOptions>): Promise<string> {
    const defaultOptions: QRCodeToDataURLOptions = {
        margin: 3,       // 二维码边距（模块数）
        scale: 10,        // 缩放因子（每个模块的像素数）
        errorCorrectionLevel: 'M', // 错误纠正级别
        color: {
            dark: '#000000ff',  // 前景色
            light: '#ffffffff'  // 背景色
        },
        type: 'image/png' // 输出格式
    };

    const finalOptions = { ...defaultOptions, ...options };

    return await QRCode.toDataURL(value, finalOptions);
}

/**
 * 生成条形码或二维码
 *
 * 根据指定的类型生成相应的条形码或二维码图像。
 * 如果生成失败，会返回一个包含错误提示的图像。
 *
 * @param type - 条形码类型（'code128', 'code39', 'ean13', 'ean8', 'qr'）
 * @param value - 要编码的数据值
 * @param height - 条形码高度（像素），默认 100，仅对条形码有效
 * @param qrOptions - 二维码专用选项，仅在 type='qr' 时有效
 * @returns Promise<string> - 返回条形码/二维码的 base64 Data URL
 */
export async function generateBarcode(
    type: string,
    value: string,
    height: number = 100,
    qrOptions?: Partial<QRCodeToDataURLOptions>
): Promise<string> {
    // 如果是二维码类型，直接调用二维码生成函数
    if (type === 'qr') {
        return generateQRCode(value, qrOptions);
    }

    // 创建临时 canvas 元素用于绘制条形码
    const canvas = document.createElement('canvas');

    // 检查是否使用默认值，如果是则使用对应类型的标准默认值
    const isDefaultValue = Object.values(DEFAULT_VALUES).includes(value) || value === '123456789';
    if (isDefaultValue && DEFAULT_VALUES[type]) {
        value = DEFAULT_VALUES[type];
    }

    try {
        // 使用 JsBarcode 生成条形码
        JsBarcode(canvas, value, {
            format: FORMAT_MAP[type] || 'CODE128',   // 条形码格式
            displayValue: true,                      // 显示文本值
            width: 2,                                // 条形宽度
            height: height,                          // 条形码高度
            fontSize: 20,                            // 文本字体大小
            margin: 10,                              // 边距
            background: '#ffffff'                  // 背景色
        });
    } catch (e) {
        // 如果生成失败（例如数据格式不正确），生成错误提示图像
        console.warn('Barcode generation warning:', e);
        const ctx = canvas.getContext('2d');
        if (ctx) {
            canvas.width = 300;
            canvas.height = 100;
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, 300, 100);
            ctx.fillStyle = 'red';
            ctx.font = '16px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('Invalid Barcode Value', 150, 55);
        }
    }

    // 将 canvas 转换为 base64 Data URL 并返回
    return canvas.toDataURL();
}
